import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { storeStateType, useAppDispatch } from "../../redux/store";
import { useForm } from "react-hook-form";
import FacilityInput from "./InputsComponents/FacilityInputField";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
} from "@mui/material";
import * as z from "zod";
import { eventForm, IFacilityOption } from "./Types/calendarTypes";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SelectInput from "./InputsComponents/SelectInput";
import moment from "moment";
import SelectRepeat from "./InputsComponents/SelectRepeat";
import { repeatEventType } from "./Add-Calendar-Event-Modal";
import { TextareaAutosize } from "@mui/base";
import Typography from "@mui/material/Typography";

import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useTheme } from "@mui/material/styles";
import { LOAD_LATEST_SELECTED_EVENT_DATA } from "../../redux/types/schedule.types";
import CustomTimeInput from "../../components/CustomTimeInput";

dayjs.extend(customParseFormat);

const formSchema = z.object({
  id: z.any(),
  work_type: z.number().min(1, "Shift worktype is required"),
  start_time: z.string().min(1, "Shift start time is required."),
  end_time: z.string().min(1, "Shift end Time is required."),
  schedule_status: z.string().min(1, "Required Status"),
  schedule_note: z.string().min(0, ""),

  sub_facility: z
    .object({
      id: z.string(),
      facility_name: z.string(),
      depth: z.number(),
      parentId: z.string().optional().nullable(),
      matchTerms: z.string().array(),
    })
    .nullable(),
});

export type eventType = z.infer<typeof formSchema>;

const statusList = [
  { id: "1", name: "Open" },
  { id: "0", name: "Closed" },
];

export type submitListType = {
  id: string;
  name: string;
};
//this is checked in addcalendar event and used in dropdown select submit
const submitList = [
  { id: "no-repeat", name: "No Repeat" },
  { id: "repeat-tommorrow", name: "Repeat Tommorrow" },
  { id: "repeat-rest-week", name: "Repeat Rest of the Week" },
  { id: "repeat-same-day", name: "Repeat Same Day" },
  { id: "repeat-specific-days", name: "Repeat Specific Days" },
];

type EventFormPropsType = {
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  handleSubmit: Function;
  overallDate?: string;
  selectedFacility: null | IFacilityOption;
  showingEventForm: eventForm;
  setRepeatEvent: React.Dispatch<React.SetStateAction<repeatEventType>>;
  repeatEvent: repeatEventType;
};
export default function ScheduleEventForm(props: EventFormPropsType) {
  const {
    handleSubmit: handleFormSubmit,
    selectedFacility,
    showingEventForm,
    setRepeatEvent,
    repeatEvent,
  } = props;
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [renderCount, setRenderCount] = useState<number>(0);

  const [isSaveButtonClicked, setIsSaveButtonClicked] =
    useState<boolean>(false);
  const [isPublishedButtonClicked, setIsPublishedButtonClicked] =
    useState<boolean>(false);

  const { loading, work_types, latestSelectedEventData } = useSelector(
    (state: storeStateType) => state.schedule
  );

  useEffect(() => {
    if (!loading) {
      // after false save button state false
      setIsPublishedButtonClicked(false);
      setIsSaveButtonClicked(false);
    }
  }, [loading]);

  useEffect(() => {
    return () => {
      dispatch({ type: LOAD_LATEST_SELECTED_EVENT_DATA, payload: null });
    };
  }, [dispatch]);

  let worktypeName = "Worktype";
  let facilityName = "Facility";
  let subfacilityName = "Subfacility";
  const defaultValues = {
    start_time: "",
    end_time: "",
    schedule_status: latestSelectedEventData?.schedule_status ?? "1",
    work_types: [],
  };

  const [formFacility, setFormFacility] = useState<IFacilityOption | null>(
    null
  );

  const {
    register,
    setValue,
    control,
    watch,
    setError,
    formState: { errors },
    handleSubmit,
  } = useForm<any>({ defaultValues: { ...defaultValues } });

  useEffect(() => {
    if (latestSelectedEventData) {
      setValue("work_type", latestSelectedEventData?.work_type);
      setValue(
        "schedule_date",
        moment(showingEventForm.preselectedDate).format("YYYY-MM-DD")
      );
      setValue(
        "schedule_status",
        latestSelectedEventData?.schedule_status ? "1" : "0"
      );
      setValue("schedule_note", latestSelectedEventData?.schedule_note);
    }
  }, [latestSelectedEventData, setValue, showingEventForm]);

  let watchFacility = watch("facility");

  useEffect(() => {
    //to reset subfacility while facility is being selected
    // when the facility is selected then render count increase
    //by default there is 2 render due to default
    //if render exceed 2 then it means user has selected other facility
    if (renderCount > 2) {
      setValue("sub_facility", null);
    }
  }, [renderCount, setValue]);

  //setting facility value to formFacility state when facility is selected
  useEffect(() => {
    setFormFacility(watch("facility"));

    setRenderCount((preState) => preState + 1);
  }, [watchFacility, setValue, setRenderCount, setFormFacility, watch]);

  function handleSubmitForm(data: any, isPublish: boolean) {
    let isError = false;

    const parseResult: any = formSchema.safeParse(data);
    if (!parseResult.success) {
      let errorKeys = Object.keys(parseResult?.error?.formErrors?.fieldErrors);
      let errorValues: any = Object.values(
        parseResult?.error?.formErrors?.fieldErrors
      );
      for (let i = 0; i < errorKeys.length; i++) {
        setError(errorKeys[i], { type: "custom", message: errorValues[i][0] });
        isError = true;
      }
      // if (errorKeys.includes("work_type")) {
      //   setError("work_type", {
      //     type: "custom",
      //     message: `${worktypeName} is required`,
      //   });
      //   isError = true;
      // }
      // // //for checking facility after error occured
    }

    if (data.facility == null) {
      setError("facility", {
        type: "custom",
        message: `${facilityName} is required`,
      });
      isError = true;
    }
    //for checking facility individually
    if (data.sub_facility == null) {
      // setError("sub_facility", { type: "custom", message: "Facility is required." });
      // error = true;
    }

    if (data.start_time === data.end_time) {
      setError("start_time", {
        type: "custom",
        message: `Start time and end time is same`,
      });
      isError = true;
    }
    if (isError) return false;

    handleFormSubmit(data, isPublish);
  }

  function handlePublishClick() {
    handleSubmit((data) => handleSubmitForm(data, true))();
  }
  const handleButtonClick = (buttonType: "save" | "publish") => {
    if (buttonType === "save") {
      setIsSaveButtonClicked(true);
    } else setIsPublishedButtonClicked(true);
  };
  return (
    <form onSubmit={handleSubmit((data) => handleSubmitForm(data, false))}>
      <Grid container rowGap={2}>
        <Typography variant="body2" fontWeight={600} width="100%">
          {latestSelectedEventData ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonthIcon />
              {moment(latestSelectedEventData?.schedule_date).format(
                "YYYY-MM-DD"
              )}
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonthIcon />
              {showingEventForm.preselectedDate}
            </Box>
          )}
        </Typography>
        <Box component={"div"} width="100%" height="100%">
          <SelectInput
            variant="standard"
            control={control}
            setValue={setValue}
            watch={watch}
            options={work_types}
            variableName={"work_type"}
            label={`${worktypeName}`}
            errors={errors}
            defaultValue={""}
          />
        </Box>
        <Box component="div" width="100%">
          <FacilityInput
            label={`${facilityName} *`}
            variableName="facility"
            control={control}
            errors={errors}
            showParentOnly={true}
            setValue={setValue}
            defaultValue={
              latestSelectedEventData?.facility ?? `${selectedFacility?.id}`
            }
            isLoadFacilities={false}
          />
        </Box>
        <Box component="div" width="100%">
          <FacilityInput
            label={`${subfacilityName}`}
            variableName="sub_facility"
            control={control}
            errors={errors}
            showChildrenOnly={true}
            defaultValue={latestSelectedEventData?.sub_facility ?? ""}
            parentId={formFacility?.id ?? ""}
            setValue={setValue}
            isLoadFacilities={false}
          />
        </Box>

        <Box component={"div"} width="100%">
          <SelectInput
            control={control}
            setValue={setValue}
            watch={watch}
            options={statusList}
            variableName={"schedule_status"}
            label="Status"
            errors={errors}
            defaultValue={""}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CustomTimeInput
              defaultValue={latestSelectedEventData?.start_time ?? ""}
              variableName="start_time"
              label="Shift start time *"
              control={control}
              errors={errors}
              setValue={setValue}
            />
            {/* <TimeInput
              defaultValue={latestSelectedEventData?.start_time ?? ""}
              variableName="start_time"
              label="Shift start time"
              control={control}
              errors={errors}
              setValue={setValue}
            /> */}
          </Grid>
          <Grid item xs={6}>
            <CustomTimeInput
              defaultValue={latestSelectedEventData?.end_time ?? ""}
              variableName="end_time"
              label="Shift end time *"
              control={control}
              errors={errors}
              setValue={setValue}
            />
            {/* <TimeInput
              defaultValue={latestSelectedEventData?.end_time ?? ""}
              variableName="end_time"
              label="Shift end time"
              control={control}
              errors={errors}
              setValue={setValue}
            /> */}
          </Grid>
        </Grid>

        <TextareaAutosize
          aria-label="Note"
          minRows={2}
          placeholder="Note"
          style={{
            width: "100%",
            padding: "8px",
            backgroundColor: "transparent",
            color: theme?.palette?.text?.primary,
          }}
          {...register("schedule_note")}
        />
        <FormHelperText error={!!errors["schedule_note"]}>
          {errors["schedule_note"] ? errors["schedule_note"].message : ""}
        </FormHelperText>
        {!latestSelectedEventData && (
          <SelectRepeat
            submitOptionList={submitList}
            setRepeatEvent={setRepeatEvent}
            repeatEvent={repeatEvent}
          />
        )}
        <div className="button-wrapper" style={{ width: "100%" }}>
          <Grid
            container
            mt={2}
            justifyContent="flex-end"
            alignItems="center"
            className="submit-button-wrapper"
            gap={1}
          >
            <Box sx={{ position: "relative" }}>
              <Button
                type="submit"
                // form="customfieldform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={loading}
                onClick={() => {
                  handleButtonClick("save");
                }}
              >
                Save
              </Button>
              {loading && isSaveButtonClicked && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "success",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Box sx={{ position: "relative" }}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                startIcon={
                  <PublishIcon sx={{ height: "15px", width: "15px" }} />
                }
                disabled={loading}
                onClick={() => {
                  handlePublishClick();
                  handleButtonClick("publish");
                }}
              >
                Save & Publish
              </Button>
              {loading && isPublishedButtonClicked && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "success",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Grid>
        </div>
      </Grid>
    </form>
  );
}
